import React, { useContext, useEffect, useState } from "react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import axios from "axios";
import "./PoDetails.scss";
import { BASE_URL, CargoPickupTypes, CargoCustomsStatus, formatToUTCDateExtended, poStatus } from "../../constants";
import { formatDate } from "@telerik/kendo-intl";
import { PoForm } from "./PoForm";
import { ShipmentForm } from "./ShipmentForm";
import { PackageList } from "./PackageList";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DocumentForm } from "./DocumentForm";
import { UserIcon, CalendarIcon } from "@heroicons/react/solid";
import { confirmAlert } from "react-confirm-alert";
import AppContext from "../../context/app-context";
import { Tooltip } from "@progress/kendo-react-tooltip";
import Modal from "react-modal";
import KendoLoader from "../KendoLoader";
import AuditHistory from "../shared/AuditHistory";

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
  },
};

const defaultPo = {
  poNo: "",
  vesselId: "",
  customerId: "",
  customerName: "",
  supplierId: "",
  totalValue: 0.0,
  receivedDate: null,
  currency: { code: "USD" },
  currencyCode: "USD",
  cargoType: 100,
  shipmentInfo: { modeOfTransport: 100 },
  documents: [],
  packages: [],
  status: "Active",
  cancelDescription: "",
  cargoPickupType: null,
  cargoCustomsStatus: null,
  cargoCustomsStatusObject: null,
  cargoCustomsStatusCode: null,
  isApproved: true,
};
const TooltipContentTemplate = (props) => {
  return (
    <a className="font-medium p-3">
      <span className="">{props.title}</span>
    </a>
  );
};
export default function PoDetails({ job, closeModal }) {
  const authCtx = useContext(AppContext);
  let navigate = useNavigate();
  let { id } = useParams();
  // When a PO is created from a Job's PO tab, this carries the originating job
  // id so we can signal that tab to auto-import the new PO (see JobPoes).
  const [searchParams] = useSearchParams();
  const originJobId = searchParams.get("jobId");
  const [po, setPo] = useState(defaultPo);
  const [docState, setDocState] = useState(-1);
  const [modified, setModified] = useState(false);
  const [loading, setLoading] = useState(false); 
  const [dataLoad, setDataLoad] = useState(false);
  const [open, setOpen] = useState(null);
  const [pendingNoteText, setPendingNoteText] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    savePo();
  };

  // Helper to determine if selected warehouse is Amsterdam / EU
  const isAmsterdamWarehouse = () => {
    return (
      po.warehouseCity &&
      (po.warehouseCity.toLowerCase().includes("amsterdam") ||
        po.warehouseCity.toLowerCase().includes("ams"))
    );
  };

  const savePo = async () => {
    const payload = {
      ...po,
      receivedDate: po.receivedDate
        ? formatToUTCDateExtended(po.receivedDate)
        : null,
      // Extract the enum value from the object for the API
      cargoCustomsStatus: po.cargoCustomsStatusObject?.id || null,
      cargoCustomsStatusCode: po.cargoCustomsStatusObject?.code || null,
    };
    
    
    
    const saveNote = async (cargoId) => {
      const trimmed = pendingNoteText.trim();
      if (!trimmed) return;
      try {
        const res = await axios.post(`${BASE_URL}/cargo/${cargoId}/notes`, { noteText: trimmed });
        if (res.status === 200) {
          setPo((prev) => ({ ...prev, notes: [...(prev.notes || []), res.data] }));
          setPendingNoteText("");
        }
      } catch {
        toast.error("PO saved, but the note could not be saved.");
      }
    };

    if (id && id !== "new") {
      if (!po.id) po.id = id;
      setLoading(true);
      try {
        let res = await axios.put(`${BASE_URL}/cargo`, payload);
        if (res.status === 200) {
          await saveNote(id);
          toast.success("PO updated successfully!");
          setModified(false);
          // Trigger audit refresh
          if (window.refreshAuditData) window.refreshAuditData();
        }
      } catch (e) {
        if (e.response?.status === 400) {
          toast.error(e.response.data);
        } else {
          toast.error("An error occured while updating PO!");
        }
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(true);
      try {
        const res = await axios.post(`${BASE_URL}/cargo`, payload);
        if (res.status === 200) {
          await saveNote(res.data);
          toast.success("PO created successfully!");
          setModified(false);
          setLoading(false);
          // Signal the originating Job tab (if any) to auto-import this PO.
          if (originJobId) {
            localStorage.setItem(
              "arc_job_new_po",
              JSON.stringify({ jobId: originJobId, poId: res.data, at: Date.now() })
            );
          }
          navigate(`/po/${res.data}`);
          // Trigger audit refresh after navigation
          setTimeout(() => {
            if (window.refreshAuditData) window.refreshAuditData();
          }, 100);
        }
      } catch (e) {
        if (e.response.status === 400) {
          toast.error(e.response.data);
        } else {
          toast.error("An error occured while creating PO!");
        }
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    // new po
    const loadPo = async () => {
      setDataLoad(true);
      if (id !== "new") {
        const res = await axios.get(`${BASE_URL}/cargo/${id}`);
        const cargoCustomsStatusObj = CargoCustomsStatus.find(
          (x) => x.id === res.data.cargoCustomsStatus
        );      
        setPo({
          ...res.data,
          creationDate: res.data.creationDate
            ? new Date(res.data.creationDate)
            : null,
          receivedDate: res.data.receivedDate
            ? new Date(res.data.receivedDate)
            : null,
          vessel: { id: res.data.vesselId, name: res.data.vesselName },
          supplier: { id: res.data.supplierId, name: res.data.supplierName },
          warehouse: { id: res.data.warehouseId, name: res.data.warehouseName },
          currency: res.data.currencyCode ? { code: res.data.currencyCode } : { code: "USD" },
          currencyCode: res.data.currencyCode || "USD",
          coo: { code: res.data.cooCode, name: res.data.cooName },
          cargoPickupTypeObject: CargoPickupTypes.find(
            (x) => x.id === res.data.cargoPickupType
          ),
          cargoCustomsStatusObject: cargoCustomsStatusObj,
          shipmentInfo: {
            ...res.data.shipmentInfo,
            originAgent: {
              id: res.data.shipmentInfo?.originAgentId,
              name: res.data.shipmentInfo?.originAgentName,
            },
            detinationAgent: {
              id: res.data.shipmentInfo?.detinationAgentId,
              name: res.data.shipmentInfo?.detinationAgentName,
            },
          },
          status: res.data.status === null ? "Active" : res.data.status,
          // cancelDescription:
          //   res.data.cancelDescription === null
          //     ? ""
          //     : res.data.cancelDescription,
        });
      } else {
        if (authCtx.profile.role === 300)
          setPo({
            ...po,
            isApproved: false,
            warehouse: authCtx.profile.warehouse,
            warehouseId: authCtx.profile.warehouse.id,
            agentId: authCtx.profile.warehouse.agentId,
            agentName: authCtx.profile.warehouse.agentName,
            warehouseCity: authCtx.profile.warehouse.cityName,
          });
      }
      setDataLoad(false);
    }; 
    loadPo();
  }, [id]);
  useEffect(() => {
    if (docState >= 0) savePo(po);
  }, [docState]);

  const onDocumentUploaded = (newDocs) => {
    const cargoDocs = newDocs.map((doc) => {
      return {
        documentID: doc.id,
        cargoID: po.id,
        fileName: doc.fileName,
        size: doc.size,
      };
    });
    setPo({ ...po, documents: [...po.documents, ...cargoDocs] });
    setDocState(docState + 1);
  };

  const onDocumentDeleted = (docId) => {
    const exDocIndex = po.documents.findIndex((x) => x.documentID === docId);
    po.documents.splice(exDocIndex, 1);
    setPo({ ...po, documents: [...po.documents] });
    savePo(po);
  };

  const onPackageAdded = (newPkg) => {
    po.packages.push(newPkg);
  };

  const handleChange = (e) => {
    setPo({
      ...po,
      [e.target.name]: e.target.value,
    });
  };
  const onStatusChange = (i) => {
    if (i === "Active") {
      const activeJobPackages = po.packages.filter(
        (x) => x.activeJobId != null && x.jobStatus === "Active"
      );
      if (!activeJobPackages || activeJobPackages.length === 0)
        setOpen("cancelConfirm");
      else {
        confirmAlert({
          customUI: ({ onClose }) => {
            return (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                  <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">
                    Active Job Information
                  </h2>
                  <p className="text-gray-600 text-xs mb-6 text-center">
                    To cancel, please remove the PO from the active job.
                    <br />
                    <strong className="block my-2">Job(s):</strong>
                    <div className="text-gray-700 grid grid-cols-3 gap-3">
                      {activeJobPackages.map((x) => (
                        <a
                          target="_blank"
                          href={`#/job/${x.activeJobId}`}
                          className="text-blue-500 underline cursor-pointer"
                        >
                          {x.jobNo}
                        </a>
                      ))}
                    </div>
                  </p>
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                    >
                      Ok
                    </button>
                  </div>
                </div>
              </div>
            );
          },
        });
      }
    } else {
      confirmAlert({
        title: "Confirm to Activate",
        message: "Are you sure to do this.",
        buttons: [
          {
            label: "Yes",
            onClick: () => {
              setModified(true);
              setPo({ ...po, status: "Active", cancelDescription: "" });
            },
          },
          {
            label: "No",
          },
        ],
      });
    }
  };

  const onCancelClick = () => {
    if (modified === true) {
      confirmAlert({
        title: "Discard Changes",
        message: "You have unsaved changes.Do you want to Continue.",
        buttons: [
          {
            label: "Yes",
            onClick: () => {
              if (!job) navigate(`/poes/all`);
              else closeModal();
            },
          },
          {
            label: "No",
          },
        ],
      });
    } else {
      if (!job) navigate(`/poes/all`);
      else closeModal();
    }
  }; return (
    <div className="min-h-full h-full relative bg-gray-50">
      {dataLoad && <KendoLoader />}

      {/* Modern Header Card */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Left side - PO Info */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${po.status === "Active" ? "bg-green-500" : "bg-orange-500"
                  } animate-pulse`}></div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">
                    {id !== "new" ? `PO #${po.poNo}` : "New Purchase Order"}
                  </h1>
                  <div className="flex items-center gap-3 mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${po.status === "Active"
                        ? "bg-green-100 text-green-800"
                        : "bg-orange-100 text-orange-800"
                      }`}>
                      {po.status}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${po.isApproved
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-yellow-100 text-yellow-800"
                      }`}>
                      {po.isApproved ? "✓ Approved" : "⏳ Pending Approval"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Metadata */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <UserIcon className="h-4 w-4 text-blue-500" />
                <span className="font-medium">Created by:</span>
                <span className="text-gray-900">
                  {id !== "new" ? po.createdUserName : authCtx.profile.displayname}
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <CalendarIcon className="h-4 w-4 text-blue-500" />
                <span className="font-medium">Created on:</span>
                <span className="text-gray-900">
                  {id !== "new"
                    ? formatDate(new Date(po.creationDate), "MMM dd, yyyy")
                    : formatDate(new Date(), "MMM dd, yyyy")}
                </span>
              </div>
            </div>

            {/* Cancel tooltip */}
            {po.status === "Canceled" && (
              <div className="flex items-center">
                <Tooltip
                  content={(props) => (
                    <TooltipContentTemplate title={props.title} />
                  )}
                  openDelay={50}
                  position="bottom"
                  anchorElement="target"
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 transition-colors">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-5 h-5 text-red-600"
                      title={po.cancelDescription}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                      />
                    </svg>
                  </div>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      </div>

      <Tabs className="po-tabs ">
        <div className="w-full sticky top-7 flex justify-between items-center pt-3 mb-2">
          <div></div>          <TabList className="po-tab border-0 w-1/3 flex justify-center items-center">
            <Tab className="po-tab font-medium text-base bg-transparent text-gray-500 my-2 mx-3">
              Details
            </Tab>
            {id && (
              <Tab className="po-tab font-medium text-base bg-transparent text-gray-500 my-2 mx-3.5">
                Documents
              </Tab>
            )}
            {id && (
              <Tab className="po-tab font-medium text-base bg-transparent text-gray-500 my-2 mx-3.5">
                Audit
              </Tab>
            )}
          </TabList>
          <div className="w-fit flex gap-2 h-full mr-2">
            {!po.isApproved && authCtx.profile.role !== 300 && (
              <div className="sm:w-32">
                <button
                  type="button"
                  className="ml-4 inline-flex float-right w-full justify-center py-2 px-4 border border-green-500 shadow-sm text-sm font-semibold rounded-md text-white bg-green-500 hover:bg-green-700 hover:text-white focus:outline-none disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none" // disabled={
                  onClick={() => setPo((pre) => ({ ...pre, isApproved: true }))}
                >
                  Approve
                </button>
              </div>
            )}
            <div className="sm:w-32">
              <button
                type="button"
                className="inline-flex w-full disabled:opacity-50 disabled:cursor-not-allowed justify-center py-2 px-4 border border-lime-700 bg-lime-700 border-transparent shadow-sm text-sm font-semibold rounded-md text-white hover:bg-lime-900 hover:border-lime-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" // disabled={
                onClick={onSubmit}
                disabled={
                  !po.packages.length > 0 ||
                  po.poNo === "" ||
                  po.vesselId === "" ||
                  po.customerId === "" ||
                  po.supplierId === "" ||
                  (isAmsterdamWarehouse() && !po.cargoCustomsStatusObject)
                }
              >
                {loading && (
                  <svg
                    aria-hidden="true"
                    role="status"
                    class="mr-2 inline h-5 w-5 animate-spin text-green-400"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    ></path>
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="#FFF"
                    ></path>
                  </svg>
                )}
                Save
              </button>
            </div>
            <div className="sm:w-32">
              <button
                type="button"
                className="ml-4 inline-flex float-right w-full justify-center py-2 px-4 border border-black shadow-sm text-sm font-semibold rounded-md text-white bg-black hover:bg-black hover:text-white focus:outline-none disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none" // disabled={
                onClick={onCancelClick}
              >
                Close
              </button>
            </div>
            <div className="relative flex gap-1 items-center">
              {/* {po.status === "Active" ? ( */}
              <button
                id="dropdownDefault"
                data-dropdown-toggle="dropdown"
                class={`${po.status === "Canceled"
                    ? "bg-green-500 border-green-600"
                    : "bg-red-500 border-red-600"
                  } flex h-9 rounded-md border-2 text-white w-28 font-semibold text-sm justify-center items-center gap-2 cursor-pointer`}
                type="button"
                onClick={() => onStatusChange(po.status)}
              >
                {po.status === "Active" ? "Cancel" : "Activate"}
              </button>

              {/* {show && (
                <div class="z-50 w-28 h-fit overflow-hidden border border-gray-200 absolute top-9 divide-y divide-gray-300 origin-top-right right-0 mt-2 rounded-md shadow-2xl py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <ul
                    class="py-1 text-sm text-gray-700"
                    aria-labelledby="dropdownDefault"
                  >
                    {poStatus.map((i) => (
                      <li key={i.id}>
                        <button
                          id={i.id}
                          type="submit"
                          class="hover:bg-gray-200 px-4 py-3 w-full text-left text-sm text-gray-700 border-b flex items-center gap-3 disabled:cursor-not-allowed"
                          onClick={() => onStatusChange(i)}
                        >
                          {i.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )} */}
            </div>
          </div>
        </div>

        <TabPanel>
          <div className="flex flex-col mb-5">
           
            <div className="flex">
              <PoForm
                po={po}
                setPo={setPo}
                setModified={setModified}
                pendingNoteText={pendingNoteText}
                onPendingNoteChange={setPendingNoteText}
              ></PoForm>
              <div className="xl:w-1/4 px-2 border-l-2 md:w-2/5 sm:w-3/6 xs:w-3/6">
                <PackageList
                  po={po}
                  setPo={setPo}
                  packageAdded={onPackageAdded}
                  id={id}
                />
              </div>
            </div>
          </div>
          <Modal
            isOpen={open !== null}
            //onAfterOpen={afterOpenModal}
            onRequestClose={() => setOpen(null)}
            style={customStyles}
            contentLabel="Example Modal"
            ariaHideApp={false}
          >
            {open === "cancelConfirm" ? (
              <div className="flex flex-col px-3 py-5 w-[30vw] overflow-hidden rounded-lg shadow-[0_0_4px_rgba(0,0,0,0.1)] border border-slate-300 mt-3">
                <h1>Cancel PO</h1>
                <textarea
                  rows={3}
                  name="cancelDescription"
                  placeholder="Please provide a reason for cancelling the purchase order"
                  id="cancelDescription"
                  value={po.cancelDescription}
                  onChange={handleChange}
                  className="block w-full h-40 shadow-sm sm:text-sm text-xs font-normal text-neutral-700 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                />
                <div className="w-full mt-4 flex justify-end">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="inline-flex h-9 w-28 float-right justify-center py-2 px-4 border-2 border-sky-600 shadow-sm text-sm font-semibold rounded-md bg-sky-600 text-white focus:outline-none disabled:opacity-50"
                      onClick={() => {
                        setModified(true);
                        setPo({
                          ...po,
                          status: "Canceled",
                        });
                        setOpen(null);
                      }}
                      disabled={po.cancelDescription === ""}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="inline-flex h-9 w-28 float-right justify-center py-2 px-4 border-2 border-black shadow-sm text-sm font-semibold rounded-md bg-black text-white focus:outline-none disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none"
                      onClick={() => setOpen(null)}
                    >
                      Close
                    </button>
                  </div>
                  
                </div>
              </div>
            ) : open === "activeJobInfo" ? (
              <div></div>
            ) : null}
          </Modal>
        </TabPanel>
        {/* <TabPanel>
          <ShipmentForm po={po} setPo={setPo} submit={onSubmit}></ShipmentForm>
        </TabPanel> */}
        {id && (<TabPanel>
          <DocumentForm
            documents={po.documents}
            documentUploaded={onDocumentUploaded}
            documentDeleted={onDocumentDeleted}
            setState={setPo}
            isPoDocument={true}
          ></DocumentForm>
        </TabPanel>
        )}        {id && (
          <TabPanel>
            <AuditHistory entityId={id} entityType="po" />
          </TabPanel>
        )}
      </Tabs>
    </div>
  );
}
